import * as aes256 from "aes256";
import http from "@/http/default/axios";
import { firebaseConfig } from "@/firebase/firebaseConfig.js";
import Store from "@/store/store.js";
import localStorage from "@/db/aisea";
import moment from "moment";

const encryptKey = process.env.VUE_APP_ENCRYPT_KEY;
const db = localStorage.getInstance();

class AuthService {
  ADDON_IDS = {
    PORT_MASTER: "1",
    VESSEL_REPORT: "2",
    SECURE_DATAFLOW: "3",
    REGISTERED_TRAVERSER: "4",
    POLARIS: "5",
    ECO_SHIP: "6",
    CREW_TIER1: "7",
    CREW_TIER2: "8",
    CREW_TIER3: "9",
    GKMG_GRID_MAP: "10",
    GKMG_WEATHER_FORECAST: "11",
    GKMG_PORT_MASTER: "12",
    GKMG_ANCHOR_MANAGEMENT: "13",
    GKMG_BASE_STATION: "14",
    GKMG_CAMERA: "15",
    GKMG_RADAR_GRID: "16",
    GKMG_PRIVATE_PLOT: "17",
    GKMG_AIS_FOR_SIGNAL: "20",
  };

  async hasUserInfoInLocalDb() {
    let res = await db.auth.get(1);
    return res !== undefined;
  }

  async isExpiryToken() {
    let res = await db.auth.get(1);
    let from = moment(res.updated_at);
    let now = moment();
    let diff = now.diff(from, "minutes");
    return diff >= 60 ? true : false;
  }

  async getIdToken() {
    let res = await db.auth.get(1);
    return aes256.decrypt(encryptKey, res.idToken);
  }

  async getRefreshToken() {
    try {
      let res = await db.auth.get(1);
      return aes256.decrypt(encryptKey, res.refreshToken);
    } catch {
      return null;
    }
  }

  async getUser() {
    let res = await db.auth.get(1);
    if (res === undefined) {
      return null;
    }
    return res.user;
  }

  async getRememberIds() {
    let res = await db.auth.get(1);
    if (res === undefined) {
      return null;
    }
    return res.rememberIds;
  }

  async getAddons() {
    let res = await db.auth.get(1);
    if (res === undefined) {
      return null;
    }
    return res.addons;
  }

  // indexedDBに保存した有料・無料情報を取得
  async getIsFree() {
    let res = await db.auth.get(1);
    if (res === undefined) {
      return null;
    }
    return res.isFree;
  }

  async saveTokens(authResult) {
    db.auth.put({
      id: 1,
      idToken: aes256.encrypt(encryptKey, authResult.idToken),
      refreshToken: aes256.encrypt(encryptKey, authResult.refreshToken),
      updated_at: new Date(),
    });
  }

  async saveAuthInfo(authResult) {
    db.auth.put({
      id: 1,
      idToken: aes256.encrypt(encryptKey, authResult.idToken),
      refreshToken: aes256.encrypt(encryptKey, authResult.refreshToken),
      user: authResult.user,
      addons: authResult.addons,
      rememberIds: authResult.rememberIds,
      updated_at: new Date(),
      isFree: authResult.isFree,
      isDebug: authResult.isDebug,
    });
  }

  async saveUser(user) {
    db.auth.update(1, {
      user: user,
    });
  }

  async updateTokens(authResult) {
    db.auth.update(1, {
      idToken: aes256.encrypt(encryptKey, authResult.idToken),
      refreshToken: aes256.encrypt(encryptKey, authResult.refreshToken),
      updated_at: new Date(),
    });
  }

  async renewToken(force) {
    let result = await this.isExpiryToken();
    if (result || force) {
      let refreshToken = await this.getRefreshToken();
      const params = new URLSearchParams();
      params.append("grant_type", "refresh_token");
      params.append("refresh_token", refreshToken);
      let res;
      try {
        res = await http.post(
          "https://securetoken.googleapis.com/v1/token?key=" +
            firebaseConfig.apiKey,
          params,
        );
      } catch (error) {
        return false;
      }
      let authResult = {
        idToken: res.data.access_token,
        refreshToken: res.data.refresh_token,
      };
      await this.updateTokens(authResult);
      Store.commit("SET_ID_TOKEN", { idToken: res.data.access_token });
      return true;
    } else {
      return true;
    }
  }

  async saveAddons(addons) {
    db.auth.update(1, {
      addons: addons,
    });
  }

  async hasAddon(addonId) {
    let res = await db.auth.get(1);
    let found = res.addons.find((v) => v.id === addonId);
    return typeof found !== "undefined";
  }

  async clearAuth() {
    await db.auth.clear();
  }

  // ログアウトしてもindexedDBに保存が必要なデータを取得
  async getPersistentData() {
    try {
      const rememberIds = await this.getRememberIds();
      const persistentData = rememberIds ? { rememberIds } : null;
      return persistentData;
    } catch {
      return null;
    }
  }

  // ログアウトしてもindexedDBに保存が必要なデータ以外を削除
  async clearNonPersistentData() {
    try {
      const persistentData = await this.getPersistentData();
      if (persistentData) {
        await db.auth.put({ id: 1, ...persistentData });
      } else {
        db.auth.clear(); // 保存が必要なデータがない場合はclear
      }
      return true;
    } catch {
      return false;
    }
  }

  async deleteDb() {
    await db.delete();
  }
}

export default new AuthService();
